import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import { Button, H3, Text } from '@payaca/ambrosia';
import { FC } from 'react';

type DesignerSummaryProps = {
  panelsCount: number;
  arrayKwp: number | null;
  annualKwh: number | null;
  annualSavings: number | null;
  equipmentSubtotal: number | null;
  currencySymbol: string;
  generationBasis: string;
  /** Combined AC output of the inverters (the totals bar shows it once). */
  inverterAcKw?: number | null;
  compact?: boolean;
  /** Shown when generation is unavailable; empty shows nothing. */
  generationMessage?: string;
  onEquipmentClick?: () => void;
};

const DesignerSummary: FC<DesignerSummaryProps> = (props) => {
  const {
    panelsCount,
    arrayKwp,
    annualKwh,
    annualSavings,
    equipmentSubtotal,
    currencySymbol,
    generationBasis,
    inverterAcKw,
    compact = false,
    generationMessage,
    onEquipmentClick,
  } = props;
  const wholeNumber = usePreferredNumberFormat({ maximumFractionDigits: 0 });
  const systemSize = usePreferredNumberFormat({
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });

  const equipment =
    equipmentSubtotal == null
      ? '—'
      : `${currencySymbol}${wholeNumber.format(equipmentSubtotal)}`;

  if (compact) {
    const chips: { label: string; value: string }[] = [
      { label: 'Panels', value: String(panelsCount) },
      {
        label: 'kWp',
        value: arrayKwp == null ? '—' : systemSize.format(arrayKwp),
      },
      {
        label: 'kWh/yr',
        value: annualKwh == null ? '—' : wholeNumber.format(annualKwh),
      },
      {
        label: 'kW AC',
        value: inverterAcKw == null ? '—' : systemSize.format(inverterAcKw),
      },
    ];
    return (
      <section
        aria-label="Design totals"
        className="flex flex-wrap items-start gap-x-5 gap-y-1"
      >
        {chips.map((chip) => (
          <div key={chip.label} className="min-w-0">
            <Text level="supporting">{chip.label}</Text>
            <Text>
              <strong className="whitespace-nowrap">{chip.value}</strong>
            </Text>
          </div>
        ))}
        <div className="min-w-0">
          <Text level="supporting">Equipment</Text>
          {onEquipmentClick ? (
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={onEquipmentClick}
              aria-label="Review equipment subtotal"
            >
              <strong className="whitespace-nowrap">{equipment}</strong>
            </Button>
          ) : (
            <Text>
              <strong className="whitespace-nowrap">{equipment}</strong>
            </Text>
          )}
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Design totals" className="space-y-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <div>
          <Text level="supporting">Generation / year</Text>
          <H3>
            {annualKwh == null ? '—' : `${wholeNumber.format(annualKwh)} kWh`}
          </H3>
        </div>
        <div>
          <Text level="supporting">Est. savings / year</Text>
          <H3>
            {annualSavings == null
              ? '—'
              : `~${currencySymbol}${wholeNumber.format(annualSavings)}`}
          </H3>
        </div>
      </div>
      {(annualKwh != null || generationMessage !== '') && (
        <Text level="supporting">
          {annualKwh == null
            ? (generationMessage ??
              'Generation is unavailable for this layout.')
            : `${generationBasis}. Indicative savings.`}
        </Text>
      )}
    </section>
  );
};

export default DesignerSummary;
